import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { Images } from '../utils/image-enum';
import { hasCookieSession } from '../utils/auth-session';
import {imageBase64String} from '../utils/imageBase64'
import { Enums } from '../utils/enums';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { sendOtpDto } from '../model/sendOtp-dto';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  ApiResponse,
  SaveBasicDetails,
} from '../model/response-format';
import { Navigations } from '../utils/navigations-enum';
import { QuestionTypesEnums } from 'src/app/examination/attempt-test/attempt-test.component';
import { FileDocumentDto } from '../model/file-document-dto';
import { VideoUploadService } from './VideoUploadService.service';
import { environment } from 'src/environments/environment';
import { SuperAdminNavigations } from '../utils/super-admin-navigations-enums';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { AdminPermissionDto } from '../model/admin-permission-dto';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  constructor(
    private readonly http: HttpClient,
    private readonly _http: HttpClient,
    private readonly videoUploadService: VideoUploadService
  ) {}
  baseUrl = environment.baseUrl;
  public getAssessmentSubmitDetails: any = new BehaviorSubject('');
  public breakTimeContinueDisable: any = new BehaviorSubject('');
  public getProfileDetailsForDashBoard: any = new BehaviorSubject('');
  public isChatbot: any = new BehaviorSubject(false);
  $profileUpdateFlag = new BehaviorSubject('none');
  public isSyncModalVisible = new BehaviorSubject<boolean>(false);
  public isFeedbackModalVisible = new BehaviorSubject<boolean>(false);
  public booleanBurger = new BehaviorSubject<boolean>(false);

  // ********API Url's*********
  apiUrls = {
    sendOtp: '/api/v1/auth/send-otp',
    validateOtp: '/api/v1/auth/otp/validate',
    getBasicDetails: '/api/v1/user/get-basic-details',
    validateEmail:'/api/v1/user/email/validate',
    verifyEmailOtp:'/api/v1/user/otp/verify/email/approve',
    logOut: '/api/v1/auth/iqigai/log-out',
    getProfileDetails: '/api/v1/user/get-profile-details',
    saveUser: '/api/v1/auth/iqigai/save-user',
    saveProfilePicture: '/api/v1/user/save-profile-picture',
    setMobileSetupStatus: '/api/v1/file/set-mobile-setup-status',
    createAnonymousUser: '/api/v1/auth/iqigai/create-anonymous-user/',
    getTermsAndPolicy: '/api/v1/auth/iqigai/get-terms-and-conditions',
    getRestrictedAppsList: '/api/v1/software-applications/get-all',
    saveCandidateMonitoring: '/api/v1/candidate-app-monitoring/save',
    getPublicAssessmentDetails: '/api/v1/assessment/candidate',
    candidateLogin: '/api/v1/auth/iqigai/login/candidate',
    forgotPasswordRequest: '/api/v1/auth/iqigai/forgetpassword',
    forgotPasswordOtpVerify: '/api/v1/auth/iqigai/forgetpassword/otp/verify',
    signUpUser: '/api/v1/auth/iqigai/signup',
    signupOtpVerify: '/api/v1/auth/iqigai/signup/verify/otp',
    generateSignUpOtp: '/api/v1/auth/signup/generate/otp',
    getVapiToken: '/api/v1/vapi-token',
  };

  // ********Service Methods*********

  sendOtp(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.sendOtp,
      requestPayload
    );
  }

  validateOtp(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.validateOtp,
      requestPayload
    );
  }
  verifyEmailOtp(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.verifyEmailOtp,
      requestPayload
    );
  }

  saveUser(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.saveUser,
      requestPayload
    );
  }
  validateEmail(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.validateEmail,
      requestPayload
    );
  }

  saveProfilePicture(
    requestPayload: FormData
  ): Observable<ApiResponse<FileDocumentDto>> {
    return this._http.post<ApiResponse<FileDocumentDto>>(
      this.baseUrl + this.apiUrls.saveProfilePicture,
      requestPayload
    );
  }

  getBasicDetails(): Observable<ApiResponse<SaveBasicDetails>> {
    return this.http.get<ApiResponse<SaveBasicDetails>>(
      this.baseUrl + this.apiUrls.getBasicDetails
    );
  }

  getProfileDetails(): Observable<any> {
    return this.http.get<ApiResponse<SaveBasicDetails>>(
      this.baseUrl + this.apiUrls.getProfileDetails
    );
  }

  logoutUser(): Observable<any> {
    const url = this.baseUrl + this.apiUrls.logOut;
    // Cookie sessions log out with POST (CSRF-checked); the response clears the cookies.
    const logout$ = hasCookieSession()
      ? this.http.post<any>(url, null)
      : this.http.get<any>(url);
    // The user asked to leave: report success even if the call fails, so every caller
    // clears local state and navigates away instead of keeping a half-dead session.
    return logout$.pipe(
      catchError(() => of({ statusCode: HttpStatusCode.Ok, message: 'Logged out' }))
    );
  }

  getRestrictedAppsList(): Observable<any> {
    return this.http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getRestrictedAppsList
    );
  }

  saveCandidateMonitoring(requestPayload): Observable<any> {
    return this._http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.saveCandidateMonitoring,
      requestPayload
    );
  }

  getPublicAssessmentDetails(token:string): Observable<any> {
    // The report page shows its own "Report not found" screen for 400/404.
    return this.http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getPublicAssessmentDetails + '/' + token,
      { headers: { 'X-Skip-Error-Interceptor': 'true' } }
    );
  }

  // getService Method
  getService(url: string, params?: any) {
    return this.http.get(this.baseUrl + url, { params: params }).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  // postService Method
  postService(url: string, data: any) {
    return this.http.post(this.baseUrl + url, data).pipe(
      map(
        (response: any) => {
          return response;
        },
        (error: any) => {}
      )
    );
  }

  // PatchService Method
  patchService(url: string, data: any) {
    return this.http.patch(this.baseUrl + url, data).pipe(
      map(
        (response: any) => {
          return response;
        },
        (error: any) => {}
      )
    );

  }

  // deleteService Method
  deleteService(url: string, params?: any) {
    return this.http.delete(this.baseUrl + url, { params: params }).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  // constructor() { }
  // environment.brand is defined on every environment.*.ts (not just LINDE's) -
  // override these centrally so every template using Images.FRACTAL_LOGO /
  // IQIGAI_AI_LOGO / IQIGAI_LOGO / LOGO_NEW_HEADER picks up the active
  // environment's brand without touching each template individually. For the
  // default iqigai environments these values are identical to the enum's own
  // literals, so this is a no-op there.
  public get Images(): typeof Images {
    return {
      ...Images,
      FRACTAL_LOGO: environment.brand.logoUrl,
      IQIGAI_AI_LOGO: environment.brand.logoUrl,
      IQIGAI_LOGO: environment.brand.chatLogoUrl,
      LOGO_NEW_HEADER: environment.brand.headerLogoUrl,
    } as typeof Images;
  }
  public get imageBase64String(): typeof imageBase64String {
    return imageBase64String;
  }
  // Same as Images above - BRAND_NAME is used app-wide (browser tab titles via
  // `${Enums.BRAND_NAME} || PageName`, and some marketing copy).
  public get Enums(): typeof Enums {
    return { ...Enums, BRAND_NAME: environment.brand.appName } as typeof Enums;
  }
  public get Navigations(): typeof Navigations {
    return Navigations;
  }
  public get SuperAdminNavigations(): typeof SuperAdminNavigations {
    return SuperAdminNavigations;
  }

  _showTermsModal = new BehaviorSubject<boolean>(false);
  _showPolicyModal = new BehaviorSubject<boolean>(false);
  _title = new BehaviorSubject<string>('Default Title');
  show$ = this._showTermsModal.asObservable();
  showPolicy$ = this._showPolicyModal.asObservable();
  title$ = this._title.asObservable();

  open(title: any) {
    console.log(title, 'title');
    if (title === 'Terms And Condition') {
      this._showTermsModal.next(true);
    }
    if (title === 'Privacy Policy') {
      this._showPolicyModal.next(true);
    }

    this._title.next(title);
  }
  getBgcolorBasedOnTheCustomerName(
    name: string,
    saturation = 50,
    lightness = 50
  ) {
    let hash = 0;
    for (let i = 0; i < name?.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }

    return `background-color:hsl(${hash % 360}, ${saturation}%, ${lightness}%)`;
  }

  close() {
    this._showTermsModal.next(false);
    this._showPolicyModal.next(false);
  }

  public get QuestionTypesEnums(): typeof QuestionTypesEnums {
    return QuestionTypesEnums;
  }

  private checkedUsersTOEditTheStatus = new Subject<any>();
  data$ = this.checkedUsersTOEditTheStatus.asObservable();

  sendDataToParent(data: any) {
    this.checkedUsersTOEditTheStatus.next(data);
  }

  private sendDataParentToChild = new Subject<any>();
  dataForChild$ = this.sendDataParentToChild.asObservable();

  sendDataToChild(dataForChild: any) {
    this.sendDataParentToChild.next(dataForChild);
  }

  private sendAssessmentData = new Subject<any>();
  assessmentData$ = this.sendAssessmentData.asObservable();

  sendAssessmentDataToChild(assessmentData: any) {
    this.sendAssessmentData.next(assessmentData);
  }

  encryptValue(data: string) {
    return btoa(data);
  }

  decryptValue(data: string) {
    return atob(data);
  }

  setMobileSetupStatus(driveId): Observable<any> {
  //  const websocketSessionId = localStorage.getItem('websocketSessionId');
    // let assessmentDriveSetId = sessionStorage.getItem('assessmentDriveSetId');
    let userAssessmentDriveSetId = sessionStorage.getItem('userAssessmentDriveSetId');
    return this.http.get(
      this.baseUrl +
        this.apiUrls.setMobileSetupStatus +
        '/true/' + userAssessmentDriveSetId
    );
  }

  createAnonymousUser(name: any): Observable<any> {
    return this.http.post(
      this.baseUrl + this.apiUrls.createAnonymousUser + name,
      null
    );
  }

  getTermsAndPolicy(): Observable<any> {
    return this.http.get<ApiResponse<SaveBasicDetails>>(
      this.baseUrl + this.apiUrls.getTermsAndPolicy
    );
  }

  clearLocalStorageOnUnload() {
    localStorage.clear();
    sessionStorage.clear();
  }

  private searchSubject = new BehaviorSubject<string>('');
  searchTerm$ = this.searchSubject.asObservable();

  updateSearchTerm(term: string) {
    this.searchSubject.next(term);
  }

  private dataSource = new BehaviorSubject<any[]>([]);
  currentData = this.dataSource.asObservable();

  changeData(data: any[]) {
    this.dataSource.next(data);
  }

  private addToGroup = new Subject<any>();
  groupModal$ = this.addToGroup.asObservable();

  activateGroupModal(data: any) {
    this.addToGroup.next(data);
  }

  passBoolean(value: boolean) {
    this.booleanBurger.next(value);
  }

  fetchRestrictedApps(): Promise<string[]> {
    return new Promise((resolve) => {
      this.getRestrictedAppsList().subscribe((res: any) => {
        if (res && res.statusCode === HttpStatusCode.Ok) {
          const restrictedApps = res.data.map((app: string) =>
            app.toLowerCase().replace(/[^a-z0-9]/g, ' ')
          );
          resolve(restrictedApps);
        } else {
          resolve([]);
        }
      });
    });
  }

  screenSources: any;
  restrictedApps: string[] | null = null;

  /** Returns the restricted-list entry found in `meta`, matched on whole words so
   *  "agent" can't fire inside "TextInputMenuAgent". Spaces inside a name are
   *  optional in the source ("parakeet ai" still catches "ParakeetAI") but the
   *  name must span whole words end-to-end — "microsoft edge" must not fire
   *  inside "Microsoft EdgeWebView" (the WebView2 runtime, not the browser). */
  private findRestrictedAppMatch(meta: string, restrictedApps: string[]): string {
    const padded = ` ${(meta || '').trim().replace(/\s+/g, ' ')} `;

    for (const restrictedApp of restrictedApps || []) {
      const name = this.normalizeLabel(restrictedApp);
      if (!name) continue;
      const pattern = new RegExp(` ${name.split(' ').join('(?: )?')} `);
      if (pattern.test(padded)) return name;
    }

    return '';
  }

  /** Checked alongside the backend list, so a self-hiding tool is caught even if
   *  the list hasn't been updated yet. Only add tools that hide themselves. */
  private readonly knownStealthApps = ['parakeet ai', 'parakeetai', 'cluely'];

  private readScreenSources(): any[] {
    const raw = localStorage.getItem('screenSources');
      try {
      const parsed = raw && raw !== 'undefined' ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      console.error('Failed to parse screenSources:', raw);
      return [];
    }
  }

  private normalizeLabel(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
      }

  /** Strips the `C:\Users\<name>` / `/Users/<name>` prefix, so a candidate whose
   *  account is named after a blocked app doesn't make every process match. */
  private normalizePath(path: string): string {
    return this.normalizeLabel(path).replace(/^(?:[a-z] )?users \S+ /, '');
      }
  
  /** A blank name only counts as stealth on a process we can inspect and that no
   *  trusted vendor published — some legitimate system processes have no name. */
  private isTrustedSystemProcess(source: any): boolean {
    const company = (source?.company || '').toLowerCase();
    const path = (source?.path || '').toLowerCase();
        if (!path) return true;
    if (/microsoft|windows|apple/.test(company)) return true;
        if (/[\\/]windows[\\/]/.test(path)) return true;
    if (/^\/(?:system|usr|bin|sbin)\//.test(path)) return true; // macOS system binaries
        return false;
  }

  /** Which restricted app a process is: identity (title/name/publisher/path) matches
   *  the list, or a blank name on a non-system binary. Returns the list name. */
  private matchRestrictedSource(source: any): string {
    const list = [...(this.restrictedApps || []), ...this.knownStealthApps];

    const identity = `${this.normalizeLabel(
      `${source?.title || ''} ${source?.processName || ''} ${source?.company || ''}`
    )} ${this.normalizePath(source?.path)}`.trim();
    const matched = this.findRestrictedAppMatch(identity, list);
    if (matched) return matched;

    if (source?.hidden && !this.isTrustedSystemProcess(source)) {
          const folder = source.path
            ? source.path.split(/[\\/]/).slice(-2, -1)[0] || ''
            : '';
          const label =
            (source.company && source.company.trim()) ||
            folder ||
            source.title ||
            'hidden app';
      return this.normalizeLabel(label) || 'hidden app';
    }

    return '';
  }

  /** Every restricted app currently running, de-duplicated and display-ready.
   *  Single entry point: replaces the old visible + stealth detector pair. */
  async restrictBackgroundApps(): Promise<string[]> {
    if (this.restrictedApps === null) {
      this.restrictedApps = await this.fetchRestrictedApps();
    }

    this.screenSources = this.readScreenSources();

    const found = new Set<string>();
    for (const source of this.screenSources) {
      const name = this.matchRestrictedSource(source);
      if (name) found.add(name);
    }

    return [...found].map((name) => name.charAt(0).toUpperCase() + name.slice(1));
  }

  shouldRestrictOnAppDetection(adminPermissions?: AdminPermissionDto): boolean {
    try {
      return adminPermissions?.overrideProctoringSetting?.appDetection !== false;
    } catch {
      return true;
    }
  }

  shouldRestrictOnExternalDevice(adminPermissions?: AdminPermissionDto): boolean {
    try {
      return adminPermissions?.overrideProctoringSetting?.isExternalDeviceConnected !== false;
    } catch {
      return true;
    }
  }

   uploadProgressSubject = new BehaviorSubject<number>(0);
   uploadProgress$ = this.uploadProgressSubject.asObservable();
   totalChunks: number = 0;
   uploadedChunks: number = 0;

   updateUploadProgress() {
    if (this.totalChunks > 0) {
      const progress = Math.round((this.uploadedChunks / this.totalChunks) * 100);
      this.uploadProgressSubject.next(progress);
    }
  }

  // async uploadPendingVideos() {
  //   this.uploadedChunks = 0;
  //   this.uploadProgressSubject.next(0);

  //   const pendingCount = await this.videoUploadService.getPendingVideoChunkCount();
  //   console.log(`Pending video chunks: ${pendingCount}`);
  //   const screenPendingCount = await this.videoUploadService.getPendingScreenVideoChunkCount();
  //   console.log(`Pending screen share video chunks: ${screenPendingCount}`);

  //   this.totalChunks = pendingCount + screenPendingCount;

  //   if (pendingCount > 0 || screenPendingCount > 0) {
  //     this.isSyncModalVisible.next(true);
  //   }

  //   try {
  //     const originalCheckAndUploadSavedChunks = this.videoUploadService.checkAndUploadSavedChunks;
  //     const originalCheckAndUploadScreenSavedChunks = this.videoUploadService.checkAndUploadScreenSavedChunks;

  //     this.videoUploadService.checkAndUploadSavedChunks = async () => {
  //       await originalCheckAndUploadSavedChunks.call(this.videoUploadService);
  //       this.uploadedChunks += pendingCount;
  //       this.updateUploadProgress();
  //     };

  //     this.videoUploadService.checkAndUploadScreenSavedChunks = async () => {
  //       await originalCheckAndUploadScreenSavedChunks.call(this.videoUploadService);
  //       this.uploadedChunks += screenPendingCount;
  //       this.updateUploadProgress();
  //     };

  //     await this.videoUploadService.checkAndUploadSavedChunks();
  //     await this.videoUploadService.checkAndUploadScreenSavedChunks();

  //     this.videoUploadService.checkAndUploadSavedChunks = originalCheckAndUploadSavedChunks;
  //     this.videoUploadService.checkAndUploadScreenSavedChunks = originalCheckAndUploadScreenSavedChunks;

  //     const remainingPendingCount = await this.videoUploadService.getPendingVideoChunkCount();
  //     const remainingScreenPendingCount = await this.videoUploadService.getPendingScreenVideoChunkCount();

  //     if (remainingPendingCount === 0 && remainingScreenPendingCount === 0) {
  //       this.isSyncModalVisible.next(false);
  //     }
  //   } catch (error) {
  //     console.error('Error uploading pending videos:', error);
  //     this.isSyncModalVisible.next(false);
  //   }
  // }


  async uploadPendingVideos() {
    this.uploadedChunks = 0;
    this.uploadProgressSubject.next(0);

    const pendingCount = await this.videoUploadService.getPendingVideoChunkCount();
    const screenPendingCount = await this.videoUploadService.getPendingScreenVideoChunkCount();

    this.totalChunks = pendingCount + screenPendingCount;

    if (this.totalChunks === 0) return;

    this.isSyncModalVisible.next(true);

    const onChunkUploaded = () => {
      this.uploadedChunks++;
      this.updateUploadProgress();
    };

    try {
      await this.videoUploadService.checkAndUploadSavedChunks(onChunkUploaded);
      await this.videoUploadService.checkAndUploadScreenSavedChunks(onChunkUploaded);

      const remainingPendingCount = await this.videoUploadService.getPendingVideoChunkCount();
      const remainingScreenPendingCount = await this.videoUploadService.getPendingScreenVideoChunkCount();

      if (remainingPendingCount === 0 && remainingScreenPendingCount === 0) {
        this.isSyncModalVisible.next(false);
      }
    } catch (error) {
      console.error('Error uploading pending videos:', error);
      this.isSyncModalVisible.next(false);
    }
  }
  
  setIsChatbot(isChatbot: boolean) {
    this.isChatbot.next(isChatbot);
  }

 //Downloading the zip file via url
async downloadZipFile(s3Url: string, fileName: string): Promise<boolean> {
  try {
    // 1. Convert the Observable to a Promise
    const data: Blob = await this.http.get(s3Url, { responseType: 'blob' }).toPromise();
    
    // Check if data is null/undefined in case toPromise resolves with no data
    if (!data) {
        throw new Error('Received no data from S3 URL');
    }

    // 2. Download Logic (Success Path)
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    document.body.appendChild(a);
    
    a.setAttribute('style', 'display: none');
    a.href = url;
    a.download = fileName;
    
    a.click();
    
    // 3. Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    // 4. Return true on successful download trigger
    return true; 
    
  } catch (err) {
    // 5. Error Handling (Failure Path)
    console.error('Download failed:', err);
    // Handle error, e.g., show a user notification
    return false; // Return false on error
  }
}

 // "2025-09-26T10:12"; returns year
  getYearFromDateTimeISOString(dateTimeString: string): number {
    const date = new Date(dateTimeString);
    return date.getFullYear();
  }

  candidateLogin(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.candidateLogin,
      requestPayload
    );
  }

  generateSignUpOtp(requestPayload: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.generateSignUpOtp,
      requestPayload
    );
  }

   signUpUser(requestPayload: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.signUpUser,
      requestPayload
    );
  }

  verifySignUpOtp(requestPayload: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.signupOtpVerify,
      requestPayload,{
        headers: { 'X-Skip-Error-Interceptor': 'true' }
      }
    );
  }

  forgotPasswordRequest(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.forgotPasswordRequest,
      requestPayload
    );
  }

   forgotPasswordOtpVerify(requestPayload: sendOtpDto): Observable<ApiResponse<sendOtpDto>> {
    return this.http.post<ApiResponse<sendOtpDto>>(
      this.baseUrl + this.apiUrls.forgotPasswordOtpVerify,
      requestPayload
    );
  }

    getVapiToken(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      this.baseUrl + this.apiUrls.getVapiToken,
    );
  }

  // Trimming  string values From start and end
   trimStrings = (obj, seen = new WeakMap()) => {
  // 1. Handle non-object types (including null) early
  if (obj === null || typeof obj !== 'object') {
    return typeof obj === 'string' ? obj.trim() : obj;
  }

  // 2. Prevent infinite loops from circular references
  if (seen.has(obj)) {
    return seen.get(obj);
  }

  // 3. Handle Arrays
  if (Array.isArray(obj)) {
    const newArr = [];
    seen.set(obj, newArr); // Track the reference
    for (let i = 0; i < obj.length; i++) {
      newArr[i] = this.trimStrings(obj[i], seen);
    }
    return newArr;
  }

  // 4. Handle Objects
  const trimmedObj = {};
  seen.set(obj, trimmedObj); // Track the reference
  
  for (const [key, value] of Object.entries(obj)) {
    trimmedObj[key] = this.trimStrings(value, seen);
  }

  return trimmedObj;
}


sanitizeSingleP(input:string): string {
  if (typeof input !== "string") return input;

  // Trim whitespace
  const str = input.trim();

  // Regex to match exactly ONE <p>...</p>
  const pTagMatch = str.match(/^<p>([\s\S]*)<\/p>$/i);
  if (!pTagMatch) {
    return input; // not exactly one <p> tag
  }

  const innerContent = pTagMatch[1];

  // Reject if inner content contains:
  // 1. Any HTML tags
  // 2. Template expressions like {{ }}
  const hasHtmlTags = /<[^>]+>/.test(innerContent);
  const hasTemplates = /{{.*?}}/.test(innerContent);

  if (hasHtmlTags || hasTemplates) {
    return input;
  }

  // Valid: only plain text inside <p>
  return innerContent;
}

sanitizeForQuestionsReport(input:string): any {
  if (typeof input !== "string") return input;

  // Trim whitespace
  const str = input.trim();

  // Regex to match exactly ONE <p>...</p>
  const pTagMatch = str.match(/^<p>([\s\S]*)<\/p>$/i);
  if (!pTagMatch) {
    return input; // not exactly one <p> tag
  }

  const innerContent = pTagMatch[1];

  // Reject if inner content contains:
  // 1. Any HTML tags
  // 2. Template expressions like {{ }}
  const hasHtmlTags = /<[^>]+>/.test(innerContent);
  const hasTemplates = /{{.*?}}/.test(innerContent);

  if (hasHtmlTags || hasTemplates) {
    return{'content':input ,type:'code'};
  }

  // Valid: only plain text inside <p>
  return {'content':innerContent ,type:'simplestring'};
}

// Fuction Truncates a number to a specific number of decimal places without rounding.
truncateNumber = (num: number, decimalPlaces: number = 2): number => {
    const factor: number = Math.pow(10, decimalPlaces);
    return Math.trunc(num * factor) / factor;
};

escapeSpecialChars(input) {
  if (!input) return "";

  const isFullMathBlock = input.trim().startsWith('$') && input.trim().endsWith('$');
  
  // Expanded list to cover operations, symbols, and layouts
  const hasMathCommand = /\\(frac|sqrt|sum|int|lim|log|sin|cos|tan|alpha|beta|gamma|delta|theta|pi|sigma|omega|infty|times|div|pm|mp|le|ge|ne|approx|leftarrow|rightarrow|text|mathbf|mathcal|binom|sqrt|root)/.test(input);

  if (isFullMathBlock || hasMathCommand) {
    return input;
  }

  const specialCharsRegex = /([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/g;

  return input.replace(specialCharsRegex, (match) => {
    const charMap = {
      '$': '＄', 
      '(': '（', 
      ')': '）', 
      '*': '\\*', 
      '_': '\\_', 
      '[': '［', 
      ']': '］', 
      '{': '｛', 
      '}': '｝', 
    };

    return charMap[match] || match;
  });
}

// Custom email validator
  customEmailValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  if (!value) return null; // let required handle empty

  const emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return emailRegex.test(value)
    ? null
    : { invalidEmail: true };
}
  

}
